/* eslint-disable import/no-internal-modules */
/* eslint-disable @typescript-eslint/no-use-before-define */

import * as DropdownMenu from '@studio/design-system/dropdown-menu';
import { normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import type { Organization } from '@studio/models/organization';
import type { Room as M_Room, Room } from '@studio/models/room';
import { useLazyRef } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import { Content } from './content.jsx';
import { toData } from './data.js';
import { Trigger } from './trigger.jsx';
import type { Handler, Props, Selection, StudioDescriptor } from './types.js';

import { Panel } from '#~/account/components/settings/types.js';
import { useGetMachineAgentId } from '#~/agent/components/bridge/hooks.js';
import { inspectInitialRoomInventory, listOrganizations, listRooms } from '#~/api/agent/index.js';
import { ApiError } from '#~/api/types.js';
import { toIdFromHex } from '#~/api/index.js';
import { toOrganizationMap, toRoomRecordMap } from '#~/auth/registry-transformers.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { useDepot } from '#~/depot/hooks.js';
import { Loading } from '#~/device/components/config/loading.js';
import { isElectronApp } from '#~/electron/utils.js';
// Per rule: use-canonical-logger-not-console (ai.knowledge/platform/studio.client).
import { logger } from '#~/logger/index.js';
import { useLastRoomIdPref } from '#~/room/hook_last_id.js';
import { Paths } from '#~/routes/index.js';
import {
  createAndLinkAccountStudio,
  decideAccountStudioSelection,
  linkAndVerifyAccountStudio
} from '#~/routes/select-studio/account-studio-operations.js';
import {
  toAccountStudioPickerState,
  type AccountStudioPickerRoom
} from '#~/routes/select-studio/bootstrap-state.js';
import {
  mergeAuthoritativeBootstrapOrganizations,
  mergeAuthoritativeBootstrapRooms
} from '#~/routes/root/authoritative-bootstrap-inventory.js';
import { useCreateOrganizationPrompt } from '#~/routes/scaffold/prompts/create-organization/hooks.js';
import { useCreateRoomPrompt } from '#~/routes/scaffold/prompts/create-room/hooks.js';
import { DataSTag, Sel } from '#~/stag/index.js';
import { Ellipses } from '@studio/design-system/label';
import { Vendor, getCurrentVendor, vendorName } from '@studio/design-system/themes/vendor.js';
import { isElement } from 'react-is';
import { Logo } from './logo.js';
// ENG-4037 Phase 6: iris studio switcher opens the shared centered picker.
import {
  SwitcherPickerIris,
  type AccountStudioPickerController
} from './vendor/iris/switcher-picker-iris.jsx';

// The account-first picker remains active during boot, but current product
// direction requires the legacy navigation-only dropdown after Studio entry.
// Keep the centered picker/controller implementation dormant for likely future
// use; re-enabling this gate also requires restoring its E2E and HIL wiring.
// Per rule: verify-design-intent-before-removal (ai.knowledge/global).
const ENABLE_POST_BOOT_CENTERED_STUDIO_PICKER = false;

const apiCode = (cause: unknown): number | undefined => {
  if (cause instanceof ApiError) return cause.code;
  if (cause instanceof Error && cause.cause !== undefined) return apiCode(cause.cause);
  if (cause !== null && typeof cause === 'object' && 'code' in cause) {
    const code = Number((cause as { code: unknown }).code);
    return Number.isFinite(code) ? code : undefined;
  }
  return undefined;
};

/** Organization/Studio `<Switcher>` component. */
export const Switcher = (props: Props): ReactElement => {
  const params = useParams();
  const currentRoomId = normalizeId<M_Room['id']>(params.roomId);
  const [selectedRoomId, setSelectedRoomId] = useState<T_Maybe<Room['id']>>(currentRoomId);
  const [rooms, synchronizer, studioUser, produce] = useDepot(
    (state) => [state.roomRecords, state.synchronizer, state.user, state.produce],
    shallow
  );
  const { agentId: machineAgentId } = useGetMachineAgentId();
  const { getAccessTokenSilently, user: auth0User } = useAuth0();
  const [, setLastRoomId] = useLastRoomIdPref();
  const [pickerInventory, setPickerInventory] = useState<readonly AccountStudioPickerRoom[]>([]);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [pickerStatus, setPickerStatus] = useState<string>();
  const [pickerError, setPickerError] = useState<string>();
  const [replacement, setReplacement] = useState<AccountStudioPickerRoom>();

  const { open: openCreateOrganizationPrompt } = useCreateOrganizationPrompt();
  const { open: openCreateRoomPrompt } = useCreateRoomPrompt();

  useEffect(() => {
    setSelectedRoomId((previous) => {
      const nextRoomId = currentRoomId ?? undefined;
      return previous === nextRoomId ? previous : nextRoomId;
    });
  }, [currentRoomId]);

  /** Action handler. */
  const handler = useLazyRef(
    (): Handler => ({
      onCreateAccount(): void {
        openCreateOrganizationPrompt((id) => openCreateRoomPrompt(id));
      },
      onCreateRoom(accountId: Organization['id']): void {
        openCreateRoomPrompt(toIdFromHex(accountId)!);
      },
      onSelectRoom: async (roomId: Room['id']): Promise<void> => {
        setSelectedRoomId(roomId);
        const url = Paths.room.resolve(roomId);
        if (url) window.location.href = url;
      },
      onShowOrganizationSettings(orgId: Organization['id']): void {
        props.settings?.(Panel.Organization, orgId);
      },
      onShowRoomSettings(roomId: Room['id']): void {
        props.settings?.(Panel.Studio, roomId);
      }
    })
  ).current;

  useEffect(() => {
    if (!synchronizer.hasRegistryLoaded) return;
    if (selectedRoomId && !rooms[selectedRoomId]) setSelectedRoomId(undefined);
  }, [rooms, selectedRoomId, synchronizer.hasRegistryLoaded]);

  const refreshPickerInventory = useCallback(
    async (token: string): Promise<readonly AccountStudioPickerRoom[]> => {
      const [primary, replicaRooms, replicaOrganizations] = await Promise.all([
        inspectInitialRoomInventory(token),
        listRooms(token),
        listOrganizations(token)
      ]);
      const state = toAccountStudioPickerState('studio_access', primary.rooms, machineAgentId);
      const mergedRooms = mergeAuthoritativeBootstrapRooms(replicaRooms, primary.rooms);
      const mergedOrganizations = mergeAuthoritativeBootstrapOrganizations(
        replicaOrganizations,
        primary.ownedOrganizations
      );
      setPickerInventory(state.rooms);
      produce((draft) => {
        draft.roomRecords = toRoomRecordMap(mergedRooms);
        draft.organizations = toOrganizationMap(mergedOrganizations, studioUser);
        draft.synchronizer.hasRegistryLoaded = true;
        draft.synchronizer.isRegistryLoading = false;
      });
      return state.rooms;
    },
    [machineAgentId, produce, studioUser]
  );

  const enterLinkedStudio = useCallback(
    (roomId: Room['id']): void => {
      setLastRoomId(roomId);
      window.location.href = `${Paths.room.boot.resolve(roomId)}${window.location.search}`;
    },
    [setLastRoomId]
  );

  const handleMutationFailure = useCallback(
    async (cause: unknown, token: string | undefined): Promise<void> => {
      if (token !== undefined) {
        try {
          await refreshPickerInventory(token);
        } catch (refreshCause) {
          logger.warning(
            'Unable to refresh header Studio inventory after a mutation failure.',
            refreshCause
          );
        }
      }
      setPickerError(
        apiCode(cause) === 403
          ? 'You don’t have permission to link this Bridge.'
          : apiCode(cause) === 409
            ? 'This Studio’s Bridge status changed. Review the refreshed list and try again.'
            : 'Iris could not update this Bridge. Review the Studio status and try again.'
      );
      setPickerBusy(false);
      setPickerStatus(undefined);
    },
    [refreshPickerInventory]
  );

  const handlePickerOpen = useCallback((): void => {
    if (machineAgentId === undefined || pickerBusy) return;
    void (async () => {
      setPickerBusy(true);
      setPickerStatus('Loading studio status…');
      setPickerError(undefined);
      try {
        const token = await getAccessTokenSilently();
        await refreshPickerInventory(token);
      } catch (cause) {
        logger.warning('Unable to refresh header Studio inventory on picker open.', cause);
        setPickerError('Iris could not refresh Studio status. Close the picker and try again.');
      } finally {
        setPickerBusy(false);
        setPickerStatus(undefined);
      }
    })();
  }, [getAccessTokenSilently, machineAgentId, pickerBusy, refreshPickerInventory]);

  const handlePickerSelect = useCallback(
    (roomId: Room['id']): void => {
      const target = pickerInventory.find((room) => room.roomId === roomId);
      const decision = decideAccountStudioSelection(
        'studio_access',
        target,
        machineAgentId as Agent['id'] | undefined
      );
      switch (decision.kind) {
        case 'open':
          handler.onSelectRoom?.(roomId);
          return;
        case 'missing':
          setPickerError(
            'This Studio is no longer available. Reopen the picker to refresh the list.'
          );
          return;
        case 'denied':
          setPickerError('You don’t have permission to link a Bridge to this Studio.');
          return;
        case 'confirm_replace':
          // Unreachable under 'studio_access', which resolves every target to
          // 'open'; kept so the switch stays exhaustive.
          setPickerError(undefined);
          setReplacement(decision.target);
          return;
        case 'link_available':
          handler.onSelectRoom?.(roomId);
      }
    },
    [handler, machineAgentId, pickerInventory]
  );

  const handleReplacementConfirm = useCallback((): void => {
    const target = replacement;
    if (target === undefined || machineAgentId === undefined || pickerBusy) return;
    void (async () => {
      setPickerBusy(true);
      setPickerStatus('Connecting bridge…');
      setPickerError(undefined);
      setReplacement(undefined);
      let token: string | undefined;
      try {
        token = await getAccessTokenSilently();
        await linkAndVerifyAccountStudio({
          expectedAgentId: machineAgentId as Agent['id'],
          expectedDisplacedAgentIds: target.activeBridges.map((bridge) => bridge.id as Agent['id']),
          policy: 'replace_existing',
          roomId: target.roomId,
          token
        });
        try {
          await refreshPickerInventory(token);
        } catch (cause) {
          logger.warning('Unable to refresh header Studio inventory after a verified link.', cause);
        }
        enterLinkedStudio(target.roomId);
      } catch (cause) {
        await handleMutationFailure(cause, token);
      }
    })();
  }, [
    enterLinkedStudio,
    getAccessTokenSilently,
    handleMutationFailure,
    machineAgentId,
    pickerBusy,
    refreshPickerInventory,
    replacement
  ]);

  const handleCreateStudio = useCallback((): void => {
    if (machineAgentId === undefined || pickerBusy) return;
    void (async () => {
      setPickerBusy(true);
      setPickerStatus('Creating studio…');
      setPickerError(undefined);
      let token: string | undefined;
      let created = false;
      try {
        token = await getAccessTokenSilently();
        const result = await createAndLinkAccountStudio({
          accountId: auth0User?.sub,
          expectedAgentId: machineAgentId as Agent['id'],
          token,
          onCreated: (value): void => {
            created = true;
            setPickerInventory(
              toAccountStudioPickerState('studio_access', value.rooms, machineAgentId).rooms
            );
          }
        });
        try {
          await refreshPickerInventory(token);
        } catch (cause) {
          logger.warning(
            'Unable to refresh header Studio inventory after a verified create.',
            cause
          );
        }
        enterLinkedStudio(result.roomId);
      } catch (cause) {
        if (!created && apiCode(cause) === 403) {
          setPickerError('You don’t have permission to create a Studio.');
          setPickerBusy(false);
          setPickerStatus(undefined);
        } else {
          await handleMutationFailure(cause, token);
        }
      }
    })();
  }, [
    auth0User?.sub,
    enterLinkedStudio,
    getAccessTokenSilently,
    handleMutationFailure,
    machineAgentId,
    pickerBusy,
    refreshPickerInventory
  ]);

  const isIris = getCurrentVendor() === Vendor.IRIS;
  const accountStudioController = useMemo<AccountStudioPickerController | undefined>(() => {
    if (
      !ENABLE_POST_BOOT_CENTERED_STUDIO_PICKER ||
      !isIris ||
      !isElectronApp() ||
      machineAgentId === undefined
    ) {
      return undefined;
    }
    return {
      busy: pickerBusy,
      inventory: pickerInventory,
      localAgentId: machineAgentId as Agent['id'],
      onCreate: handleCreateStudio,
      onOpen: handlePickerOpen,
      onSelect: handlePickerSelect,
      ...(pickerStatus === undefined ? {} : { operationStatus: pickerStatus }),
      ...(pickerError === undefined ? {} : { errorMessage: pickerError }),
      ...(replacement === undefined
        ? {}
        : {
            replacement: {
              machineNames: replacement.activeBridges.map((bridge) => bridge.name),
              onCancel: (): void => setReplacement(undefined),
              onConfirm: handleReplacementConfirm
            }
          })
    };
  }, [
    handleCreateStudio,
    handlePickerOpen,
    handlePickerSelect,
    handleReplacementConfirm,
    isIris,
    machineAgentId,
    pickerBusy,
    pickerError,
    pickerInventory,
    pickerStatus,
    replacement
  ]);

  return (
    <SwitcherRoot
      {...props}
      className={twMerge('min-w-[300px]', props.className)}
      dropdownClassName={`bg-base-100 h-auto ${isIris ? 'min-w-[370px]' : 'min-w-[300px]'} flex-1`}
      handler={handler}
      selectedRoomId={selectedRoomId}
      {...(accountStudioController === undefined ? {} : { accountStudioController })}
    />
  );
};

/** Organization/Studio `<Switcher>` component. */
export const SwitcherLite = ({
  onOrganizationChange,
  onRoomChange,
  ...props
}: Props & {
  dropdownAlign?: 'end' | 'start';
  dropdownClassName?: string;
  onOrganizationChange?: (id: Organization['id']) => void;
  onRoomChange?: (id: Room['id']) => void;
  selectedOrgId?: T_Maybe<Organization['id']>;
  selectedRoomId?: T_Maybe<Room['id']>;
}): ReactElement => {
  /** Action handler. */
  const handler = useLazyRef(
    (): Handler => ({
      onSelectRoom: onRoomChange
        ? (roomId: Room['id']): void => {
            onRoomChange(roomId);
          }
        : undefined,
      onSelectOrganization: onOrganizationChange
        ? (orgId: Organization['id']): void => {
            onOrganizationChange(orgId);
          }
        : undefined
    })
  ).current;

  return (
    <SwitcherRoot
      {...props}
      handler={handler}
      selectedOrgId={props.selectedOrgId}
      selectedRoomId={props.selectedRoomId}
    />
  );
};

/** Organization/Studio `<Switcher>` component. */
export const SwitcherRoot = ({
  accountStudioController,
  dropdownAlign,
  dropdownClassName,
  handler,
  selectedOrgId,
  selectedRoomId,
  ...props
}: Props & {
  accountStudioController?: AccountStudioPickerController | undefined;
  dropdownAlign?: 'end' | 'start';
  dropdownClassName?: string;
  handler: Handler;
  selectedOrgId?: T_Maybe<Organization['id']>;
  selectedRoomId: T_Maybe<Room['id']>;
}): ReactElement => {
  const [rooms, orgs, agents, synchronizer] = useDepot(
    (state) => [state.roomRecords, state.organizations, state.agents, state.synchronizer],
    shallow
  );
  const { agentId: machineAgentId } = useGetMachineAgentId();

  const { settings: _, ...triggerProps } = props;

  // Initialize selection state immediately when props are available
  const initialSelection = useMemo((): Selection | undefined => {
    if (!selectedRoomId && !selectedOrgId) {
      return undefined;
    }

    if (selectedOrgId && !selectedRoomId) {
      const currentOrg = orgs[selectedOrgId];
      if (currentOrg) {
        return {
          organizationId: currentOrg.id,
          roomId: undefined
        };
      }
    }

    if (selectedRoomId) {
      const currentRoom = rooms[selectedRoomId];
      if (currentRoom) {
        return {
          organizationId: currentRoom.organizationId,
          roomId: currentRoom.id
        };
      }
    }

    return undefined;
  }, [orgs, rooms, selectedOrgId, selectedRoomId]);

  const [selection, setSelection] = useState<Selection | undefined>(initialSelection);

  useEffect(() => {
    setSelection(initialSelection);
  }, [initialSelection]);

  const data = toData(rooms, orgs, agents, machineAgentId);

  const orgOnly = !!(!handler.onSelectRoom && handler.onSelectOrganization);
  const hasRegistryLoaded = synchronizer.hasRegistryLoaded;
  const isRegistryLoading = synchronizer.isRegistryLoading;

  // Type guard to check if data is StudioDescriptor[]. NOTE: intentionally
  // false for an EMPTY list — the ENG-4037 centered-picker branch below also
  // relies on that so the zero-studio state keeps the legacy dropdown.
  const isStudioDescriptorArray = (d: typeof data): d is StudioDescriptor[] => {
    return Array.isArray(d) && d.length > 0 && d[0] !== undefined && 'room' in d[0];
  };

  let descriptorFromSelection: StudioDescriptor | undefined;
  let descriptorFromRoomId: StudioDescriptor | undefined;
  let descriptorFromOrg: StudioDescriptor | undefined;

  if (isStudioDescriptorArray(data)) {
    descriptorFromSelection = data.find((descriptor) => descriptor.room.id === selection?.roomId);
    descriptorFromRoomId = data.find((descriptor) => descriptor.room.id === selectedRoomId);
    descriptorFromOrg = data.find(
      (descriptor) => descriptor.organization.id === (selection?.organizationId ?? selectedOrgId)
    );
  } else if (Array.isArray(data) && data.length > 0 && data[0]) {
    // For Hierarchy format, create a StudioDescriptor-like object from the first room
    const firstHierarchy = data[0];
    const firstRoom = firstHierarchy.rooms[0];
    if (firstRoom) {
      descriptorFromOrg = {
        organization: firstHierarchy.organization,
        room: firstRoom,
        badges: [],
        status: 'offline' as const,
        // Hierarchy fallback (Hive) never renders the popped card — derive the
        // ENG-4037 fields minimally to satisfy the enriched descriptor shape.
        deviceCount: Object.keys(firstRoom.agents).length,
        isThisDevice: false,
        roles: []
      };
    }
  }

  const descriptor =
    descriptorFromSelection ??
    descriptorFromRoomId ??
    descriptorFromOrg ??
    (isStudioDescriptorArray(data) ? data[0] : undefined);

  const displayName = orgOnly ? descriptor?.organization.name : descriptor?.room.name;

  const logoName = descriptor?.organization.name ?? descriptor?.room.name ?? '';

  const logoPhoto = descriptor?.organization.photoUrl;

  const hasAnyRooms = (data ?? []).length > 0;
  const fallbackText = hasRegistryLoaded && !hasAnyRooms ? 'Create a studio' : 'Select a studio';

  const shouldShowLoading = isRegistryLoading && !displayName;
  const finalDisplayName = displayName || (shouldShowLoading ? <Loading /> : fallbackText);

  const isPtzoptics = vendorName().toLowerCase() === 'hive';
  const isIris = getCurrentVendor() === Vendor.IRIS;

  // The retained centered branch is selected only when the post-boot gate is
  // deliberately restored; boot selection is owned by /select-studio.
  if (
    ENABLE_POST_BOOT_CENTERED_STUDIO_PICKER &&
    isIris &&
    !orgOnly &&
    !!handler.onSelectRoom &&
    isStudioDescriptorArray(data)
  ) {
    return (
      <div className='flex items-center justify-center gap-12'>
        <Logo className='ml-12' name={logoName} photo={logoPhoto} />
        <span
          className={`${shouldShowLoading ? 'italic text-muted' : ''}`}
          {...DataSTag(Sel.SettingsSwitcher, 'display-name', selectedRoomId)}
        >
          {isElement(finalDisplayName) ? (
            finalDisplayName
          ) : (
            <Ellipses className='max-w-[200px]' title={finalDisplayName as string}>
              {finalDisplayName}
            </Ellipses>
          )}
        </span>
        <SwitcherPickerIris
          {...(accountStudioController === undefined ? {} : { accountStudioController })}
          descriptors={data}
          handler={handler}
          selection={selection}
          selectedRoomId={selectedRoomId ?? undefined}
          triggerProps={{
            ...triggerProps,
            hasRegistryLoaded,
            isRegistryLoading,
            selection,
            selectedOrgId,
            selectedRoomId
          }}
        />
      </div>
    );
  }

  return (
    <DropdownMenu.Root>
      {isIris ? (
        <div className='flex items-center justify-center gap-12'>
          <Logo className='ml-12' name={logoName} photo={logoPhoto} />
          <span
            className={`${shouldShowLoading ? 'italic text-muted' : ''}`}
            {...DataSTag(
              Sel.SettingsSwitcher,
              'display-name',
              orgOnly ? selectedOrgId : selectedRoomId
            )}
          >
            {isElement(finalDisplayName) ? (
              finalDisplayName
            ) : (
              <Ellipses className='max-w-[200px]' title={finalDisplayName as string}>
                {finalDisplayName}
              </Ellipses>
            )}
          </span>
          <DropdownMenu.Trigger className='group'>
            <Trigger
              {...triggerProps}
              data={data}
              hasRegistryLoaded={hasRegistryLoaded}
              isRegistryLoading={isRegistryLoading}
              orgOnly={orgOnly}
              selection={selection}
              selectedOrgId={selectedOrgId}
              selectedRoomId={selectedRoomId}
            />
          </DropdownMenu.Trigger>
        </div>
      ) : (
        <DropdownMenu.Trigger className='group'>
          <Trigger
            {...triggerProps}
            data={data}
            hasRegistryLoaded={hasRegistryLoaded}
            isRegistryLoading={isRegistryLoading}
            orgOnly={orgOnly}
            selection={selection}
            selectedOrgId={selectedOrgId}
            selectedRoomId={selectedRoomId}
          />
        </DropdownMenu.Trigger>
      )}
      <DropdownMenu.Content
        align={dropdownAlign ?? 'start'}
        alignOffset={isIris ? 5 : 10}
        {...(isIris ? { sideOffset: 20 } : {})}
        className={twMerge(props.className, dropdownClassName)}
        data-theme={isPtzoptics ? 'ptz-optics' : 'dark'}
      >
        <Content
          data={data}
          handler={handler}
          hasRegistryLoaded={hasRegistryLoaded}
          isRegistryLoading={isRegistryLoading}
          orgOnly={orgOnly}
          selection={selection}
        />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
